import { useParams } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { ChatView } from "@/components/chat/ChatView";
import { CameraCapture } from "@/components/chat/CameraCapture";
import { ImageEditor } from "@/components/chat/ImageEditor";
import { showToast } from "@/components/shared/Toast";
import { useEffect, useRef, useState, useCallback } from "react";

interface Message {
  _key: string;
  from: string;
  to: string;
  encrypted_message: string;
  nonce: string;
  date: string;
  is_system: boolean;
  message_type?: "text" | "image";
  // System message fields from server
  type?: string;
  entity_name?: string;
  entity_icon?: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
  points?: number;
}

interface PaginatedResponse {
  messages: Message[];
  hasMore: boolean;
  total: number;
}

export function ClientMessages() {
  const { jwt } = useParams();
  const { getHeaders, user } = useAuth();
  const { encryptValue, decryptValue, hasKey, encryptBinary, decryptBinary } = useEncryption(user?._key);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [capturedCanvas, setCapturedCanvas] = useState<HTMLCanvasElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Decrypt messages (DM text + system message entity names)
  // Image messages are NOT decrypted here — ChatImageBubble does lazy decryption
  const decryptMessages = useCallback(async (msgs: Message[]): Promise<Message[]> => {
    return Promise.all(msgs.map(async (msg) => {
      if (msg.is_system) {
        const entity_name = msg.entity_name ? await decryptValue(msg.entity_name) : undefined;
        const ref_name = msg.ref_name ? await decryptValue(msg.ref_name) : undefined;
        const encrypted_note = msg.encrypted_note ? await decryptValue(msg.encrypted_note) : undefined;
        return { ...msg, entity_name, ref_name, encrypted_note };
      } else if (msg.message_type === "image") {
        // Leave encrypted_message as-is; ChatImageBubble decrypts lazily
        return msg;
      } else {
        const decrypted = await decryptValue(msg.encrypted_message);
        return { ...msg, encrypted_message: decrypted };
      }
    }));
  }, [decryptValue]);

  const fetchMessages = useCallback(async (before?: string) => {
    const url = new URL(`/api/client/${jwt}/messages`, window.location.origin);
    url.searchParams.set("limit", "50");
    if (before) url.searchParams.set("before", before);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    const data: PaginatedResponse = await res.json();
    return data;
  }, [jwt, getHeaders]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchMessages().then(async (data) => {
      if (cancelled) return;
      const decrypted = await decryptMessages(data.messages);
      setMessages(decrypted);
      setHasMore(data.hasMore);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Unknown error");
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [fetchMessages, decryptMessages]);

  // Poll for new messages
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const data = await fetchMessages();
        const decrypted = await decryptMessages(data.messages);
        setMessages((prev) => {
          const existingKeys = new Set(prev.map((m) => m._key));
          const newMsgs = decrypted.filter((m) => !existingKeys.has(m._key));
          if (newMsgs.length === 0) return prev;
          return [...prev, ...newMsgs].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );
        });
        setHasMore(data.hasMore);
      } catch {
        // Silently ignore poll errors
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages, decryptMessages]);

  // Load older messages
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    try {
      const oldestDate = messages[0]!.date;
      const data = await fetchMessages(oldestDate);
      const decrypted = await decryptMessages(data.messages);
      setMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => m._key));
        const olderMsgs = decrypted.filter((m) => !existingKeys.has(m._key));
        return [...olderMsgs, ...prev];
      });
      setHasMore(data.hasMore);
    } catch (err: any) {
      showToast.error(err.message || "Failed to load older messages");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, messages, fetchMessages, decryptMessages]);

  if (loading && messages.length === 0) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const clientId = user ? `Clients/${user._key}` : "";

  const handleSend = async (text: string) => {
    try {
      // Encrypt message before sending
      const encrypted = await encryptValue(text);
      const res = await fetch(`/api/client/${jwt}/messages`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          encrypted_message: encrypted,
          nonce: "encrypted",
          message_type: "text",
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send message");
      }
      const freshData = await fetchMessages();
      const decrypted = await decryptMessages(freshData.messages);
      setMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => m._key));
        const newMsgs = decrypted.filter((m) => !existingKeys.has(m._key));
        if (newMsgs.length === 0) return prev;
        return [...prev, ...newMsgs].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      });
    } catch (err: any) {
      showToast.error(err.message || "Failed to send message");
      throw err;
    }
  };

  const handleSendImage = async (bytes: Uint8Array) => {
    setCapturedCanvas(null);
    try {
      const encryptedImg = await encryptBinary(bytes);
      if (!encryptedImg) throw new Error("Encryption key not available");

      const res = await fetch(`/api/client/${jwt}/messages`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          encrypted_message: encryptedImg,
          nonce: "encrypted",
          message_type: "image",
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send image");
      }
      const freshData = await fetchMessages();
      const decrypted = await decryptMessages(freshData.messages);
      setMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => m._key));
        const newMsgs = decrypted.filter((m) => !existingKeys.has(m._key));
        if (newMsgs.length === 0) return prev;
        return [...prev, ...newMsgs].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      });
    } catch (err: any) {
      showToast.error(err.message || "Failed to send image");
    }
  };

  return (
    <div className="h-[calc(100dvh-8.5rem)] md:h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-8rem)] flex flex-col">
      {/* Camera capture overlay */}
      {captureOpen && (
        <CameraCapture
          onCapture={(canvas) => {
            setCapturedCanvas(canvas);
            setCaptureOpen(false);
          }}
          onClose={() => setCaptureOpen(false)}
        />
      )}

      {/* Image editor overlay */}
      {capturedCanvas && (
        <ImageEditor
          sourceCanvas={capturedCanvas}
          onConfirm={handleSendImage}
          onCancel={() => setCapturedCanvas(null)}
        />
      )}

      <Card
        className="flex-1 min-h-0 flex flex-col overflow-hidden border-app-primary/15 shadow-lg shadow-app-primary/5 py-0"
      >
        <ChatView
          messages={messages}
          currentUserId={clientId}
          onSend={handleSend}
          onCaptureImage={() => setCaptureOpen(true)}
          decryptBinary={decryptBinary}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
        />
      </Card>
    </div>
  );
}
